import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, ArrowUpRight, Ban, Check, CircleCheck, ListChecks, Loader2, ReceiptText, RotateCcw, Search, ShieldAlert,
  ShieldCheck, Undo2, UserRound,
} from 'lucide-react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { formatMoney, formatTime } from '@/lib/format';
import { getErrorMessage } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import { StatusPill } from '@/components/ui/status-pill';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { notify } from '@/components/ui/notify';
import { IconTile } from './ui';

function AssignPopover({ task, onAssigned }) {
  const [open, setOpen] = useState(false);
  const [members, setMembers] = useState(null);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [savingId, setSavingId] = useState('');

  useEffect(() => {
    if (!open || members) return;
    api.get('/tasks/team-members')
      .then((response) => setMembers(Array.isArray(response.data) ? response.data : []))
      .catch((err) => setError(getErrorMessage(err, 'Could not load the team')));
  }, [open, members]);

  const visible = useMemo(() => {
    const words = query.toLowerCase().split(/\s+/).filter(Boolean);
    return (members || []).filter((member) => words.every((word) => `${member.name} ${member.role || ''}`.toLowerCase().includes(word)));
  }, [members, query]);

  const assign = async (member) => {
    setSavingId(member.id);
    try {
      await api.patch(`/tasks/${task.id}`, { assignees: [{ id: member.id, name: member.name, member_type: member.member_type }] });
      onAssigned(member);
      setOpen(false);
      notify.success(`“${task.name}” assigned to ${member.name}`);
    } catch (err) {
      notify.error('Could not assign the task', { description: getErrorMessage(err) });
    } finally {
      setSavingId('');
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" size="sm" variant="ghost" data-testid="ai-action-assign">
          <UserRound aria-hidden="true" />
          Assign
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[260px] rounded-xl border-line bg-surface p-1.5 shadow-overlay">
        <label className="mb-1 flex h-8 items-center gap-2 rounded-lg border border-line px-2 text-ink-4">
          <Search className="h-3.5 w-3.5" aria-hidden="true" />
          <input
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search people"
            aria-label="Search people"
            className="h-full min-w-0 flex-1 bg-transparent text-[13px] text-ink outline-none placeholder:text-ink-4"
          />
        </label>
        <div className="max-h-[240px] overflow-y-auto">
          {error ? <p className="px-2 py-2 text-[12.5px] text-status-red-ink">{error}</p> : null}
          {!members && !error ? <p className="px-2 py-2 text-[12.5px] text-ink-3">Loading…</p> : null}
          {members && !visible.length ? <p className="px-2 py-2 text-[12.5px] text-ink-3">No one matches.</p> : null}
          {visible.map((member) => (
            <button
              key={member.id}
              type="button"
              disabled={Boolean(savingId)}
              onClick={() => assign(member)}
              className="flex h-[34px] w-full items-center gap-2 rounded-lg px-2 text-left text-[13px] text-ink hover:bg-surface-hover disabled:opacity-60"
            >
              <span className="min-w-0 flex-1 truncate">{member.name}</span>
              {savingId === member.id ? <Loader2 className="h-3.5 w-3.5 animate-spin text-ink-3" aria-hidden="true" /> : (
                <span className="shrink-0 truncate text-[11.5px] text-ink-3">{member.role}</span>
              )}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export const PROPOSAL_TYPES = new Set(['task_proposal', 'invoice_status_proposal']);

function DetailRow({ label, children }) {
  return (
    <>
      <dt className="pt-px text-[12px] text-ink-3">{label}</dt>
      <dd className="min-w-0 text-[13px] text-ink">{children}</dd>
    </>
  );
}

function proposalSummary(action) {
  const invoice = action.invoice || {};
  if (action.type === 'task_proposal') {
    return [action.task_name, action.project_name].filter(Boolean).join(' · ');
  }
  return [
    [action.project_name, invoice.invoice_type || invoice.label].filter(Boolean).join(' · '),
    invoice.amount ? formatMoney(invoice.amount) : null,
    `${action.from_status || '—'} → ${action.to_status}`,
  ].filter(Boolean).join(' · ');
}

/**
 * "Confirm change" (25 idea 3): what the assistant is about to change, with before → after, and
 * Confirm / Cancel. Nothing is saved until Confirm. After Confirm the page swaps it for the result card.
 * onConfirm() / onCancel() → Promise (throw → the error shows here). onAskAgain(text) refills the box.
 */
export function ProposalCard({ action, onConfirm, onCancel, onAskAgain }) {
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const state = action.state || 'proposed';
  const invoice = action.invoice || {};
  const isTask = action.type === 'task_proposal';

  const run = async (kind, fn) => {
    setBusy(kind);
    setError('');
    try {
      await fn();
    } catch (err) {
      setError(getErrorMessage(err, kind === 'confirm' ? 'Could not save this change' : 'Could not cancel'));
    } finally {
      setBusy('');
    }
  };

  if (state === 'cancelled' || state === 'expired') {
    const cancelled = state === 'cancelled';
    return (
      <div
        data-testid="ai-proposal-card"
        data-state={state}
        className="flex gap-3 rounded-xl border border-line bg-surface py-3 pl-3.5 pr-3.5 shadow-[inset_3px_0_0_var(--line-2)]"
      >
        <IconTile icon={cancelled ? Ban : ShieldAlert} tone="gray" size={32} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill tone={cancelled ? 'gray' : 'amber'} label={cancelled ? 'Cancelled' : 'Not saved'} size="sm" />
            <span className="text-[12px] text-ink-3">{cancelled ? 'Nothing was changed' : (action.expired_reason || 'Nothing was changed')}</span>
          </div>
          <p className="mt-1 truncate text-[12.5px] text-ink-3" title={proposalSummary(action)}>{proposalSummary(action)}</p>
          {!cancelled && action.request && onAskAgain ? (
            <Button type="button" size="xs" variant="outline" className="mt-2" onClick={() => onAskAgain(action.request)}>
              <RotateCcw aria-hidden="true" />
              Ask again
            </Button>
          ) : null}
        </div>
      </div>
    );
  }

  const applying = state === 'applying' || busy === 'confirm';
  return (
    <div
      data-testid="ai-proposal-card"
      data-state={state}
      data-action-type={action.type}
      className="rounded-xl border border-line bg-surface py-3.5 pl-4 pr-4 shadow-[inset_3px_0_0_var(--status-amber)]"
    >
      <div className="flex flex-wrap items-center gap-2.5">
        <IconTile icon={ShieldCheck} tone="amber" size={28} />
        <p className="text-[14px] font-bold text-ink">Confirm change</p>
        <p className="text-[12px] text-ink-3">Nothing is saved until you confirm</p>
      </div>
      <dl className="mt-3 grid grid-cols-[88px_minmax(0,1fr)] gap-x-4 gap-y-2 border-t border-line-2 pt-3 sm:ml-[38px]">
        {isTask ? (
          <>
            <DetailRow label="New task"><span className="font-semibold">{action.task_name}</span></DetailRow>
            <DetailRow label="Project">{action.project_name || '—'}</DetailRow>
            {action.invoice ? (
              <DetailRow label="Invoice">
                {[invoice.invoice_type || invoice.label, invoice.amount ? formatMoney(invoice.amount) : null].filter(Boolean).join(' · ')}
              </DetailRow>
            ) : null}
            <DetailRow label="Assigned">Unassigned · Open · Medium priority</DetailRow>
          </>
        ) : (
          <>
            <DetailRow label="Invoice">
              <span className="font-semibold">{[action.project_name, invoice.invoice_type || invoice.label].filter(Boolean).join(' · ') || 'Invoice'}</span>
              {invoice.reference ? <span className="text-ink-3"> · {invoice.reference}</span> : null}
            </DetailRow>
            <DetailRow label="Amount"><span className="font-bold tabular-nums">{formatMoney(invoice.amount)}</span></DetailRow>
            <DetailRow label="Status">
              <span className="inline-flex flex-wrap items-center gap-1.5">
                <StatusPill status={action.from_status} domain="invoice" size="sm" />
                <ArrowRight className="h-3.5 w-3.5 text-ink-3" aria-label="to" />
                <StatusPill status={action.to_status} domain="invoice" size="sm" />
              </span>
            </DetailRow>
          </>
        )}
        <DetailRow label="After"><span className="text-ink-3">Recorded in History · can be undone</span></DetailRow>
      </dl>
      <div className="mt-3 flex flex-wrap items-center gap-2 sm:ml-[38px]">
        <Button type="button" size="sm" onClick={() => run('confirm', onConfirm)} disabled={Boolean(busy) || applying} data-testid="ai-proposal-confirm">
          {applying ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Check aria-hidden="true" />}
          {applying ? 'Saving…' : isTask ? 'Create task' : 'Confirm update'}
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={() => run('cancel', onCancel)} disabled={Boolean(busy) || applying} data-testid="ai-proposal-cancel">
          Cancel
        </Button>
      </div>
      {error ? <p role="alert" className="mt-2 text-[12.5px] font-medium text-status-red-ink sm:ml-[38px]">{error}</p> : null}
    </div>
  );
}

/**
 * Result of a change the assistant made — "Task created" / "Invoice updated" with Open and Undo.
 * onUndo() → Promise (throws on failure; the card shows the error).
 * Proposals ("Confirm change") render as ProposalCard.
 */
export default function AiActionCard({ action, canUndo, canAssign, onUndo, onConfirm, onCancel, onAskAgain }) {
  if (PROPOSAL_TYPES.has(action?.type)) {
    return <ProposalCard action={action} onConfirm={onConfirm} onCancel={onCancel} onAskAgain={onAskAgain} />;
  }
  return <ResultCard action={action} canUndo={canUndo} canAssign={canAssign} onUndo={onUndo} />;
}

function ResultCard({ action, canUndo, canAssign, onUndo }) {
  const navigate = useNavigate();
  const [undoing, setUndoing] = useState(false);
  const [undoError, setUndoError] = useState('');
  const [assignee, setAssignee] = useState('');
  const undone = Boolean(action?.undone_at);

  const runUndo = async () => {
    setUndoing(true);
    setUndoError('');
    try {
      await onUndo();
    } catch (err) {
      setUndoError(getErrorMessage(err, 'Could not undo this change'));
    } finally {
      setUndoing(false);
    }
  };

  // Once the task was edited here (assigned), undoing its creation is refused by the undo history.
  const undoButton = canUndo && !undone && !assignee ? (
    <Button type="button" size="sm" variant="ghost" onClick={runUndo} disabled={undoing} data-testid="ai-action-undo">
      {undoing ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Undo2 aria-hidden="true" />}
      Undo
    </Button>
  ) : null;

  const when = formatTime(action?.created_at || action?.task?.created_at, { fallback: '' });

  if (action?.type === 'task_created') {
    const task = action.task || {};
    const invoice = action.invoice;
    const assigned = assignee || task.assigned_to_name;
    const meta = [
      action.project_name || null,
      invoice ? `Invoice: ${invoice.invoice_type || invoice.label}` : (task.invoice_reference ? `Invoice: ${task.invoice_reference}` : null),
      invoice && invoice.amount ? formatMoney(invoice.amount) : null,
      assigned || 'Unassigned',
    ].filter(Boolean);
    return (
      <div
        data-testid="ai-action-card"
        data-action-type="task_created"
        className={cn(
          'flex gap-3 rounded-xl border border-line bg-surface py-3 pl-3.5 pr-3.5 shadow-[inset_3px_0_0_var(--status-green)]',
          undone && 'opacity-75 shadow-[inset_3px_0_0_var(--line-2)]'
        )}
      >
        <IconTile icon={ListChecks} tone={undone ? 'gray' : 'green'} size={32} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            {undone ? (
              <StatusPill status="Undone" size="sm" />
            ) : (
              <StatusPill tone="green" label="Task created" icon={CircleCheck} size="sm" />
            )}
            {when ? <span className="text-[11.5px] text-ink-3">{when}</span> : null}
            {!undone ? <StatusPill status={task.status || 'Open'} domain="task" size="sm" className="ml-auto" /> : null}
          </div>
          <p className={cn('mt-1 text-[14px] font-bold leading-snug text-ink', undone && 'line-through decoration-ink-4')}>{task.name}</p>
          <p className="mt-0.5 text-[12.5px] text-ink-3">
            {undone ? 'The task was removed.' : meta.join(' · ')}
          </p>
          {!undone ? (
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <Button type="button" size="sm" variant="outline" onClick={() => navigate(`/tasks?task=${encodeURIComponent(task.id)}`)} data-testid="ai-action-open">
                <ArrowUpRight aria-hidden="true" />
                Open in Tasks
              </Button>
              {canAssign ? <AssignPopover task={task} onAssigned={(member) => setAssignee(member.name)} /> : null}
              {undoButton}
            </div>
          ) : null}
          {undoError ? <p role="alert" className="mt-2 text-[12.5px] font-medium text-status-red-ink">{undoError}</p> : null}
        </div>
      </div>
    );
  }

  if (action?.type === 'invoice_updated') {
    const invoice = action.invoice || {};
    const title = [action.project_name, invoice.invoice_type || invoice.label].filter(Boolean).join(' · ') || 'Invoice';
    return (
      <div
        data-testid="ai-action-card"
        data-action-type="invoice_updated"
        className={cn(
          'flex gap-3 rounded-xl border border-line bg-surface py-3 pl-3.5 pr-3.5 shadow-[inset_3px_0_0_var(--status-green)]',
          undone && 'opacity-75 shadow-[inset_3px_0_0_var(--line-2)]'
        )}
      >
        <IconTile icon={ReceiptText} tone={undone ? 'gray' : 'navy'} size={32} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            {undone ? <StatusPill status="Undone" size="sm" /> : <StatusPill tone="green" label="Invoice updated" icon={CircleCheck} size="sm" />}
            {when ? <span className="text-[11.5px] text-ink-3">{when}</span> : null}
          </div>
          <p className="mt-1 text-[14px] font-bold leading-snug text-ink">{title}</p>
          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[12.5px] text-ink-3">
            {invoice.amount ? <span className="font-semibold tabular-nums text-ink-2">{formatMoney(invoice.amount)}</span> : null}
            {action.previous_status ? (
              <>
                <StatusPill status={action.previous_status} domain="invoice" size="sm" />
                <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
              </>
            ) : null}
            <StatusPill status={action.status} domain="invoice" size="sm" />
            {undone ? <span>· status put back</span> : null}
          </div>
          {!undone ? (
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {action.project_id ? (
                <Button type="button" size="sm" variant="outline" onClick={() => navigate(`/projects/${action.project_id}?tab=invoicing`)}>
                  <ArrowUpRight aria-hidden="true" />
                  Open project invoicing
                </Button>
              ) : null}
              {undoButton}
            </div>
          ) : null}
          {undoError ? <p role="alert" className="mt-2 text-[12.5px] font-medium text-status-red-ink">{undoError}</p> : null}
        </div>
      </div>
    );
  }

  return null;
}
